"use client";

import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  FaBars,
  FaTimes,
  FaCaretDown,
  FaPhone,
  FaChevronRight,
  FaArrowLeft,
} from "react-icons/fa";

// Helper function to determine if a color is dark (for logo color switching)
function isDarkColor(color: string): boolean {
  // Convert rgb/rgba to values
  const rgbMatch = color.match(/\d+/g);
  if (!rgbMatch || rgbMatch.length < 3) return false;

  const r = parseInt(rgbMatch[0]);
  const g = parseInt(rgbMatch[1]);
  const b = parseInt(rgbMatch[2]);

  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return true if dark (luminance < 0.5)
  return luminance < 0.5;
}

// Section 1: Primary Treatments
const section1Categories = [
  { name: "Hair Transplant", href: "/hair-transplant" },
  { name: "Facial Hair Transplant", href: "/facial-hair-transplant" },
  { name: "Non-Surgical Treatments", href: "/non-surgical-solutions" },
  { name: "Hair Transplant Research", href: "/hair-transplant-research" },
];

// Section 2: Hair Loss & Scalp Conditions
const section2Categories = [
  { name: "Hair Loss Causes", href: "/hair-loss-causes" },
  { name: "Hair & Scalp Disorders", href: "/hair-and-scalp-disorders" },
  { name: "Scalp Conditions", href: "/common-scalp-conditions" },
  { name: "Hair & Scalp Infections", href: "/hair-and-scalp-infections" },
  {
    name: "Hormonal Hair Conditions",
    href: "/hormonal-and-systemic-conditions",
  },
];

// Section 3: Alopecia & Pattern Hair Loss
const section3Categories = [
  { name: "Types of Alopecia", href: "/alopecia-condition" },
];

// Section 4: About & Clinic
const section4Categories = [
  { name: "Why Trichos", href: "/why-trichos" },
  { name: "About Trichos", href: "/about-trichos" },
];

// Menu structure mapping
const menuStructure: Record<string, Array<{ name: string; href: string }>> = {
  "Primary Treatments": section1Categories,
  "Hair Loss & Scalp Conditions": section2Categories,
  "Alopecia & Pattern Hair Loss": section3Categories,
  "About & Clinic": section4Categories,
};

export default function MenuNavbar() {
  const pathname = usePathname();
  const isHomepage = pathname === "/";
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isScrollingUp, setIsScrollingUp] = useState(false);
  const lastScrollYRef = useRef(0);
  const [isFooterNear, setIsFooterNear] = useState(false);
  const [sectionBgColor, setSectionBgColor] = useState<string | null>(null);
  const [isPressReleasePage, setIsPressReleasePage] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  // Check if we're on a press release slug page
  useEffect(() => {
    if (!pathname) return;
    
    const pathParts = pathname.split("/").filter(Boolean);
    // Check if we're on a slug page (has 2 segments: category and slug)
    if (pathParts.length >= 2) {
      const category = pathParts[0];
      const slug = pathParts[1];
      
      // Check if category is dr-john-watts-media (press release category)
      if (category === "dr-john-watts-media") {
        setIsPressReleasePage(true);
      } else {
        setIsPressReleasePage(false);
      }
    } else {
      setIsPressReleasePage(false);
    }
  }, [pathname]);

  // Track scroll direction, footer proximity, and section background colors
  useEffect(() => {
    // Helper function to get background color from an element (including nested elements)
    const getBackgroundColor = (element: Element): string | null => {
      // First check inline styles
      const inlineStyle = (element as HTMLElement).style;
      if (
        inlineStyle.backgroundColor &&
        inlineStyle.backgroundColor !== "transparent"
      ) {
        return inlineStyle.backgroundColor;
      }

      // Check computed style
      const computedStyle = window.getComputedStyle(element);
      const bgColor = computedStyle.backgroundColor;

      if (
        bgColor &&
        bgColor !== "rgba(0, 0, 0, 0)" &&
        bgColor !== "transparent"
      ) {
        return bgColor;
      }

      // Check background-image for gradients (extract first color)
      const bgImage = computedStyle.backgroundImage;
      if (bgImage && bgImage !== "none") {
        // Try to extract color from gradient
        const gradientMatch = bgImage.match(/rgba?\([^)]+\)|#[0-9A-Fa-f]{3,6}/);
        if (gradientMatch) {
          return gradientMatch[0];
        }
      }

      // Check nested children with backgrounds
      const children = Array.from(element.children);
      for (const child of children) {
        const childBg = getBackgroundColor(child);
        if (childBg) return childBg;
      }

      return null;
    };

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const lastScrollY = lastScrollYRef.current;

      // Track if user has scrolled (for press release pages) - any direction
      setIsScrolled(currentScrollY > 10);

      // Track scroll direction for shadow effect only
      if (currentScrollY < lastScrollY && currentScrollY > 10) {
        setIsScrollingUp(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 10) {
        setIsScrollingUp(false);
      } else if (currentScrollY <= 10) {
        setIsScrollingUp(false);
      }

      // Check if footer is near (within 100px of navbar)
      const footerElement = document.querySelector("footer");
      if (footerElement) {
        const footerRect = footerElement.getBoundingClientRect();
        const navbarHeight = 60; // Approximate navbar height
        // Footer is near navbar when its top is within navbar height range
        const footerNear =
          footerRect.top <= navbarHeight &&
          footerRect.top >= -footerElement.offsetHeight;
        setIsFooterNear(footerNear);

        if (footerNear) {
          setSectionBgColor(null);
          lastScrollYRef.current = currentScrollY;
          return;
        }
      }

      // Only run section color detection on homepage
      if (isHomepage) {
        // Find all sections and detect which one is active
        const sections = document.querySelectorAll("section");
        const navbarTop = 60; // Navbar height
        const viewportTop = 0;

        // Find the section that's currently at the top of the viewport (or closest to navbar)
        let activeSection: Element | null = null;
        let minDistance = Infinity;

        sections.forEach((section) => {
          const rect = section.getBoundingClientRect();
          // Check if section is visible and near the navbar
          // Section is active if its top is near or above the navbar
          if (rect.top <= navbarTop + 200 && rect.bottom > viewportTop) {
            const distance = Math.abs(rect.top - navbarTop);
            if (distance < minDistance) {
              minDistance = distance;
              activeSection = section;
            }
          }
        });

        // Map sections to their specific colors
        const getSectionColor = (section: Element): string | null => {
          // First check the section element itself for TreatmentPhilosophy
          const sectionInlineStyle = (section as HTMLElement).style;
          const sectionComputedStyle = window.getComputedStyle(section);
          const sectionBgImage =
            sectionInlineStyle.backgroundImage ||
            sectionComputedStyle.backgroundImage;
          if (
            sectionBgImage &&
            sectionBgImage !== "none" &&
            sectionBgImage.includes("linear-gradient") &&
            (sectionBgImage.includes("#F8F9FA") ||
              sectionBgImage.includes("#E8ECF1"))
          ) {
            const hasPurpleCards = section.querySelectorAll(
              '[style*="gradient"], [class*="violet"], [class*="purple"]'
            );
            if (hasPurpleCards.length > 0) {
              return "#E1D2EE";
            }
          }

          // Check EducationSection (Knowledge section) - #5820AF
          const sectionClasses = section.className || "";
          if (
            sectionClasses.includes("py-16") &&
            sectionClasses.includes("md:py-24")
          ) {
            const hasEducationContent =
              section.textContent?.includes("Learn") ||
              section.textContent?.includes("Knowledge") ||
              section.textContent?.includes("Education") ||
              section.querySelector('[class*="violet-950"]');
            if (hasEducationContent) {
              return "#5820AF";
            }
          }

          // Check direct children divs first (for ProductShowcaseSection and ProcessSection)
          const directChildren = Array.from(section.children).filter(
            (child) => child.tagName === "DIV"
          );

          for (const div of directChildren) {
            const inlineStyle = (div as HTMLElement).style;
            const computedStyle = window.getComputedStyle(div);

            // Check for ProductShowcaseSection (gradient with #F9EAAD or #F6E8AE)
            const bgImage =
              inlineStyle.backgroundImage || computedStyle.backgroundImage;
            if (
              bgImage &&
              bgImage !== "none" &&
              (bgImage.includes("#F9EAAD") ||
                bgImage.includes("#F6E8AE") ||
                bgImage.includes("F9EAAD") ||
                bgImage.includes("F6E8AE")) &&
              (bgImage.includes("#CDDAE2") || bgImage.includes("CDDAE2"))
            ) {
              return "#F6E8AE";
            }

            // Check for ProcessSection (backgroundColor: #9E6625)
            const bgColor =
              inlineStyle.backgroundColor || computedStyle.backgroundColor;
            if (
              bgColor &&
              bgColor !== "rgba(0, 0, 0, 0)" &&
              bgColor !== "transparent"
            ) {
              const bgColorLower = bgColor.toLowerCase();
              if (
                bgColorLower.includes("#9e6625") ||
                bgColorLower.includes("158, 102, 37") ||
                bgColorLower === "rgb(158, 102, 37)"
              ) {
                return "#9E6625";
              }
            }
          }

          // Fallback: Check all divs
          const divs = section.querySelectorAll("div");
          for (const div of Array.from(divs)) {
            const inlineStyle = (div as HTMLElement).style;
            const computedStyle = window.getComputedStyle(div);

            // Check for ProductShowcaseSection
            const bgImage =
              inlineStyle.backgroundImage || computedStyle.backgroundImage;
            if (
              bgImage &&
              bgImage !== "none" &&
              (bgImage.includes("#F9EAAD") ||
                bgImage.includes("#F6E8AE") ||
                bgImage.includes("F9EAAD") ||
                bgImage.includes("F6E8AE")) &&
              (bgImage.includes("#CDDAE2") || bgImage.includes("CDDAE2"))
            ) {
              return "#F6E8AE";
            }

            // Check for ProcessSection
            const bgColor =
              inlineStyle.backgroundColor || computedStyle.backgroundColor;
            if (
              bgColor &&
              bgColor !== "rgba(0, 0, 0, 0)" &&
              bgColor !== "transparent"
            ) {
              const bgColorLower = bgColor.toLowerCase();
              if (
                bgColorLower.includes("#9e6625") ||
                bgColorLower.includes("158, 102, 37") ||
                bgColorLower === "rgb(158, 102, 37)"
              ) {
                return "#9E6625";
              }
            }
          }

          return null;
        };

        // Get background color from the active section
        if (activeSection) {
          const sectionColor = getSectionColor(activeSection);
          // Debug: log section detection
          // console.log("Active section:", activeSection, "Detected color:", sectionColor);

          if (sectionColor) {
            setSectionBgColor(sectionColor);
          } else {
            // If no specific section color found and we're at the top, use default
            if (currentScrollY < 100) {
              setSectionBgColor("#F9FAFB");
            }
            // Otherwise, try to get background color using the existing method
            else {
              const bgColor = getBackgroundColor(activeSection);
              if (
                bgColor &&
                bgColor !== "rgba(0, 0, 0, 0)" &&
                bgColor !== "transparent"
              ) {
                setSectionBgColor(bgColor);
              }
              // If still no color found, don't change (keep previous)
            }
          }
        } else {
          // No section found, use default #F9FAFB only if at top
          if (currentScrollY < 100) {
            setSectionBgColor("#F9FAFB");
          }
        }
      } else {
        // On non-homepage pages, use default background color
        if (currentScrollY < 100) {
          setSectionBgColor("#F9FAFB");
        } else {
          // Keep default background for other pages
          setSectionBgColor(null);
        }
      }

      // Update lastScrollY at the end
      lastScrollYRef.current = currentScrollY;
    };

    // Initial call
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomepage, isPressReleasePage]);

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      // Reset to main menu when opening
      setSelectedCategory(null);
    }
  };

  const handleLinkClick = () => {
    setIsMenuOpen(false);
    setSelectedCategory(null);
  };

  const handleCategoryClick = (categoryTitle: string) => {
    setSelectedCategory(categoryTitle);
  };

  const handleBackClick = () => {
    setSelectedCategory(null);
  };

  // Render main menu (category titles)
  const renderMainMenu = () => {
    return (
      <div className="space-y-2">
        {Object.keys(menuStructure).map((categoryTitle) => (
          <button
            key={categoryTitle}
            onClick={() => handleCategoryClick(categoryTitle)}
            className="flex w-full items-center justify-between rounded-lg px-4 py-4 text-2xl font-semibold text-gray-900 transition-all hover:bg-gray-100"
          >
            <span>{categoryTitle}</span>
            <FaChevronRight className="ml-2 text-gray-400" />
          </button>
        ))}
      </div>
    );
  };

  // Render subcategory menu
  const renderSubcategoryMenu = () => {
    if (!selectedCategory) return null;
    const subcategories = menuStructure[selectedCategory] || [];

    return (
      <div className="space-y-2">
        {subcategories.map((subcategory) => (
          <Link
            key={subcategory.name}
            href={subcategory.href}
            onClick={handleLinkClick}
            className="flex w-full items-center justify-between rounded-lg px-4 py-4 text-2xl font-semibold text-gray-900 transition-all hover:bg-gray-100"
          >
            <span>{subcategory.name}</span>
            <FaChevronRight className="ml-2 text-gray-400" />
          </Link>
        ))}
      </div>
    );
  };

  return (
    <>
      <nav
        className={`sticky top-0 z-40 flex w-full items-center justify-between px-4 py-3 transition-all duration-300 ${
          isFooterNear
            ? "bg-black"
            : sectionBgColor
              ? ""
              : `${isScrollingUp ? "shadow-md" : ""}`
        }`}
        style={{
          position: "sticky",
          top: 0,
          left: 0,
          right: 0,
          width: "100%",
          maxWidth: "100vw",
          zIndex: 40,
          ...(isFooterNear
            ? {
                backgroundColor: "#000000",
                boxShadow: `
                  0 0 20px rgba(255, 0, 150, 0.4),
                  0 0 40px rgba(255, 100, 0, 0.3),
                  0 0 60px rgba(255, 200, 0, 0.25),
                  0 0 80px rgba(0, 255, 150, 0.2),
                  0 0 100px rgba(0, 150, 255, 0.15),
                  0 0 120px rgba(150, 0, 255, 0.1),
                  0 4px 20px rgba(255, 255, 255, 0.3)
                `,
              }
            : isPressReleasePage && isScrolled
              ? {
                  backgroundColor: "#ffffff",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                }
              : sectionBgColor
                ? {
                    backgroundColor: sectionBgColor,
                    ...(isScrollingUp
                      ? { boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" }
                      : {}),
                  }
                : {
                    backgroundColor: "#F9FAFB",
                    ...(isScrollingUp
                      ? { boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" }
                      : {}),
                  }),
        }}
      >
        {/* Logo - Extreme Left */}
        <Link href="/" className="flex items-center">
          <Image
            src={
              isFooterNear || (sectionBgColor && isDarkColor(sectionBgColor))
                ? "/images/logo/trichos-logo-white.svg"
                : "/images/logo/trichos-logo-black.svg"
            }
            alt="Trichos Logo"
            width={160}
            height={40}
            className="h-5 w-auto"
            style={{
              imageRendering: "crisp-edges",
            }}
            priority
            unoptimized
          />
        </Link>

        {/* Right Side: Call, Book, and Hamburger Menu */}
        <div className="flex items-center gap-2">
          {/* Call Button */}
          <a
            href="tel:09000566767"
            className={`flex items-center justify-center rounded-full p-2.5 transition-colors ${
              isFooterNear || (sectionBgColor && isDarkColor(sectionBgColor))
                ? "bg-white/20 text-white hover:bg-white/30"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
            aria-label="Call"
          >
            <FaPhone className="size-5" />
          </a>
          {/* Book Button */}
          <Link
            href="/appointment"
            className={`flex items-center justify-center rounded-full px-4 py-2.5 text-sm font-medium transition-colors ${
              isFooterNear || (sectionBgColor && isDarkColor(sectionBgColor))
                ? "bg-white/20 text-white hover:bg-white/30"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
            aria-label="Book Appointment"
          >
            Book
          </Link>
          {/* Hamburger Menu */}
          <button
            onClick={handleMenuToggle}
            className={`flex items-center justify-center rounded-lg p-2 transition-colors ${
              isFooterNear || (sectionBgColor && isDarkColor(sectionBgColor))
                ? "text-white hover:bg-white/20"
                : "text-gray-700 hover:bg-gray-100"
            }`}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <FaTimes className="size-6" />
            ) : (
              <FaBars className="size-6" />
            )}
          </button>
        </div>
      </nav>

      {/* Slide-out Menu - Right Aligned */}
      <div
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-md transform bg-white shadow-2xl transition-transform duration-300 ease-in-out ${
          isMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Menu Header */}
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-4">
          {selectedCategory ? (
            <button
              onClick={handleBackClick}
              className="flex items-center gap-2 text-xl font-bold text-gray-900 transition-colors hover:text-gray-700"
            >
              <FaArrowLeft className="size-5" />
              <span>Back</span>
            </button>
          ) : (
            <h2 className="text-xl font-bold text-gray-900">Menu</h2>
          )}
          <div className="flex items-center gap-2">
            {/* Call Button */}
            <a
              href="tel:09000566767"
              className="flex items-center justify-center rounded-full bg-gray-100 p-2.5 text-gray-700 transition-colors hover:bg-gray-200"
              aria-label="Call"
            >
              <FaPhone className="size-5" />
            </a>
            {/* Book Button */}
            <Link
              href="/appointment"
              onClick={handleLinkClick}
              className="flex items-center justify-center rounded-full bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
              aria-label="Book Appointment"
            >
              Book
            </Link>
            {/* Close Button */}
            <button
              onClick={handleMenuToggle}
              className="flex items-center justify-center rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-200"
              aria-label="Close menu"
            >
              <FaTimes className="size-5" />
            </button>
          </div>
        </div>

        {/* Menu Content - Scrollable */}
        <div className="h-[calc(100vh-73px)] overflow-y-auto px-6 py-6">
          {selectedCategory ? renderSubcategoryMenu() : renderMainMenu()}
        </div>
      </div>

      {/* Overlay - Dark background when menu is open */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50"
          onClick={handleMenuToggle}
          aria-hidden="true"
        />
      )}
    </>
  );
}
